import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Calculator, 
  FlaskConical, 
  Droplets, 
  Shield,
  RotateCcw,
  Thermometer,
  CheckCircle2,
  AlertTriangle,
  Syringe,
  Calendar,
  BookOpen,
  Beaker,
  Info
} from 'lucide-react';

// Peptide data with suggested doses
const PEPTIDES = [
  { id: 'custom', name: 'Custom / Other', suggestedDose: '', vialSizes: [], description: 'Enter your own values' },
  { id: 'aod-9604', name: 'AOD-9604', suggestedDose: '300', vialSizes: ['5'], description: 'Subcutaneous fat-loss peptide', doseRange: '250-500 mcg', price: 45 },
  { id: 'retatrutide', name: 'Retatrutide', suggestedDose: '500', vialSizes: ['30'], description: 'Triple-antagonist metabolic peptide', doseRange: '0.5-2 mg/week', price: 150 },
  { id: 'cjc-ipa', name: 'CJC-1295/Ipamorelin', suggestedDose: '300', vialSizes: ['10'], description: 'Growth hormone boosters', doseRange: '100-300 mcg each', price: 45 },
  { id: 'dsip', name: 'DSIP', suggestedDose: '200', vialSizes: ['5'], description: 'Deep Sleep-Inducing Peptide', doseRange: '100-300 mcg', price: 35 },
  { id: 'epitalon', name: 'Epitalon', suggestedDose: '500', vialSizes: ['10'], description: 'Cellular research peptide', doseRange: '5-10 mg/cycle', price: 35 },
  { id: 'ghk-cu', name: 'GHK-Cu', suggestedDose: '200', vialSizes: ['100'], description: 'Copper research peptide', doseRange: '200-400 mcg', price: 50 },
  { id: 'vip', name: 'VIP', suggestedDose: '200', vialSizes: ['10'], description: 'Nervous system & gut regulator', doseRange: '100-500 mcg', price: 70 },
  { id: 'klow', name: 'KLOW', suggestedDose: '500', vialSizes: ['80'], description: '4-in-1: KPV, TB-500, BPC-157, GHK-Cu', doseRange: '500-1000 mcg', price: 105 },
  { id: 'kpv', name: 'KPV', suggestedDose: '200', vialSizes: ['10'], description: 'Anti-inflammatory peptide', doseRange: '200-500 mcg', price: 55 },
  { id: 'mots-c', name: 'MOTS-c', suggestedDose: '5000', vialSizes: ['40'], description: 'Mitochondrial peptide', doseRange: '5-10 mg/week', price: 85 },
  { id: 'nad', name: 'NAD+', suggestedDose: '100000', vialSizes: ['500'], description: 'Cellular energy molecule', doseRange: '100-250 mg', price: 55 },
  { id: 'semax', name: 'Semax', suggestedDose: '500', vialSizes: ['10'], description: 'Cognitive peptide', doseRange: '200-1000 mcg', price: 40 },
  { id: 'selank', name: 'Selank', suggestedDose: '500', vialSizes: ['10'], description: 'Anti-anxiety peptide', doseRange: '200-1000 mcg', price: 40 },
  { id: 'ss-31', name: 'SS-31 (Elamipretide)', suggestedDose: '500', vialSizes: ['50'], description: 'Mitochondrial protector', doseRange: '250-1000 mcg', price: 140 },
  { id: 'ta1', name: 'Thymosin Alpha-1', suggestedDose: '1500', vialSizes: ['10'], description: 'Immune system regulator', doseRange: '1-3 mg', price: 95 },
  { id: 'tesamorelin', name: 'Tesamorelin', suggestedDose: '2000', vialSizes: ['10'], description: 'Metabolic research peptide', doseRange: '1-2 mg', price: 80 },
  { id: 'bpc-tb-blend', name: 'BPC-157 & TB-500', suggestedDose: '500', vialSizes: ['20'], description: 'Research peptide blend', doseRange: '500-1000 mcg', price: 95 },
  { id: 'glutathione', name: 'Glutathione', suggestedDose: '200000', vialSizes: ['1500'], description: 'Master antioxidant', doseRange: '100-500 mg', price: 80 },
];

const PeptideCalculatorPage = () => {
  // Calculator State
  const [calculator, setCalculator] = useState({
    selectedPeptide: 'custom',
    peptideAmount: '',
    waterVolume: '2',
    desiredDose: '',
    dosesPerWeek: '5',
    result: null
  });

  // Get selected peptide data
  const selectedPeptideData = PEPTIDES.find(p => p.id === calculator.selectedPeptide);

  // Handle peptide selection
  const handlePeptideSelect = (peptideId) => {
    const peptide = PEPTIDES.find(p => p.id === peptideId);
    setCalculator({
      ...calculator,
      selectedPeptide: peptideId,
      desiredDose: peptide?.suggestedDose || '',
      peptideAmount: peptide?.vialSizes?.[0] || calculator.peptideAmount,
      result: null
    });
  };

  // Calculate Everything
  const calculateDosing = () => {
    const peptide = parseFloat(calculator.peptideAmount);
    const water = parseFloat(calculator.waterVolume);
    const dose = parseFloat(calculator.desiredDose);
    const frequency = parseFloat(calculator.dosesPerWeek) || 1;
    
    if (peptide > 0 && water > 0 && dose > 0) {
      const concentrationMcgMl = (peptide * 1000) / water;
      const volumePerDoseMl = dose / concentrationMcgMl;
      const volumePerDoseUl = volumePerDoseMl * 1000;
      
      // Insulin syringe units (100 units = 1 mL)
      const insulinUnits = volumePerDoseMl * 100;
      
      const totalDoses = (peptide * 1000) / dose;
      const weeksSupply = totalDoses / frequency;
      
      setCalculator({
        ...calculator,
        result: {
          concentration: concentrationMcgMl.toFixed(0),
          volumePerDoseMl: volumePerDoseMl.toFixed(3),
          volumePerDoseUl: volumePerDoseUl.toFixed(0),
          insulinUnits: insulinUnits.toFixed(1),
          totalDoses: Math.floor(totalDoses),
          weeksSupply: weeksSupply.toFixed(1),
          weeklyUnits: (insulinUnits * frequency).toFixed(0)
        }
      });
    }
  };

  const resetCalculator = () => setCalculator({
    selectedPeptide: 'custom',
    peptideAmount: '',
    waterVolume: '2',
    desiredDose: '',
    dosesPerWeek: '5',
    result: null
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Dosing & Reconstitution Calculator"
        description="Free peptide reconstitution calculator. Calculate dosing, water volume, and weekly protocols for research peptides. Includes storage guidelines and unit conversions."
        keywords="peptide calculator, reconstitution guide, peptide dosing, bacteriostatic water, insulin syringe units"
      />
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center">
              <BookOpen className="h-6 w-6 text-teal-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Dosing & Reconstitution Guide
              </h1>
              <p className="text-slate-600">Educational guide for peptide reconstitution and weekly dosing protocols</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Disclaimer */}
        <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              <strong>Research Use Only:</strong> This educational guide is provided for laboratory research purposes only. 
              All products are intended for research and are not for human or animal consumption.
            </p>
          </div>
        </div>

        {/* Step-by-Step Reconstitution Guide */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <FlaskConical className="h-6 w-6 text-teal-700" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Step-by-Step Reconstitution Guide
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: 1,
                title: "Gather Supplies",
                icon: Beaker,
                description: "You'll need: bacteriostatic water, alcohol swabs, syringes (for mixing and dosing), and your lyophilized peptide vial.",
                tip: "Always use bacteriostatic water, not sterile water, for longer shelf life."
              },
              {
                step: 2,
                title: "Clean & Prepare",
                icon: Shield,
                description: "Wipe the rubber stopper of both the peptide vial and bacteriostatic water vial with alcohol swabs. Let dry completely.",
                tip: "Work in a clean environment to prevent contamination."
              },
              {
                step: 3,
                title: "Add Water Slowly",
                icon: Droplets,
                description: "Draw your calculated amount of bacteriostatic water. Insert needle into peptide vial and release water slowly along the glass wall.",
                tip: "Never spray directly onto the powder—it can damage the peptide."
              },
              {
                step: 4,
                title: "Mix Gently",
                icon: RotateCcw,
                description: "Gently swirl the vial in circular motions until the powder is fully dissolved. The solution should be clear.",
                tip: "Never shake vigorously—this can denature the peptide."
              }
            ].map((item) => (
              <Card key={item.step} className="border-slate-100 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-10 h-10 bg-teal-700 flex items-center justify-center text-white font-bold rounded-br-xl">
                  {item.step}
                </div>
                <CardContent className="pt-12 pb-6 px-6">
                  <div className="flex items-center gap-2 mb-3">
                    <item.icon className="h-5 w-5 text-teal-700" />
                    <h3 className="font-heading font-semibold text-slate-900">{item.title}</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">{item.description}</p>
                  <div className="p-3 bg-teal-50 rounded-lg">
                    <p className="text-xs text-teal-800">
                      <strong>Pro Tip:</strong> {item.tip}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Dosing Calculator Section */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Calculator className="h-6 w-6 text-teal-700" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Dosing Calculator
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <Card className="border-slate-100">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Syringe className="h-5 w-5 text-teal-700" />
                  Calculate Your Protocol
                </CardTitle>
                <p className="text-sm text-slate-500">
                  Select your peptide for suggested dosing, or enter custom values.
                </p>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Peptide Selector */}
                <div className="space-y-2">
                  <Label>Select Peptide</Label>
                  <Select 
                    value={calculator.selectedPeptide} 
                    onValueChange={handlePeptideSelect}
                  >
                    <SelectTrigger data-testid="peptide-select">
                      <SelectValue placeholder="Choose a peptide..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PEPTIDES.map((peptide) => (
                        <SelectItem key={peptide.id} value={peptide.id}>
                          <div className="flex items-center gap-2">
                            <span>{peptide.name}</span>
                            {peptide.doseRange && (
                              <span className="text-xs text-slate-400">({peptide.doseRange})</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedPeptideData && selectedPeptideData.id !== 'custom' && (
                    <p className="text-xs text-teal-600">
                      {selectedPeptideData.description} • Suggested: {selectedPeptideData.doseRange}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="peptide-amount">Vial Size (mg)</Label>
                    <Input
                      id="peptide-amount"
                      type="number"
                      placeholder="e.g., 5"
                      value={calculator.peptideAmount}
                      onChange={(e) => setCalculator({ ...calculator, peptideAmount: e.target.value, result: null })}
                      data-testid="calc-peptide-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="water-volume">Water to Add (mL)</Label>
                    <Input
                      id="water-volume"
                      type="number"
                      placeholder="e.g., 2"
                      value={calculator.waterVolume}
                      onChange={(e) => setCalculator({ ...calculator, waterVolume: e.target.value, result: null })}
                      data-testid="calc-water-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="desired-dose">Dose per Use (mcg)</Label>
                    <Input
                      id="desired-dose"
                      type="number"
                      placeholder="e.g., 250"
                      value={calculator.desiredDose}
                      onChange={(e) => setCalculator({ ...calculator, desiredDose: e.target.value, result: null })}
                      data-testid="calc-dose-input"
                    />
                    {selectedPeptideData && selectedPeptideData.suggestedDose && (
                      <p className="text-xs text-slate-400">Suggested: {selectedPeptideData.suggestedDose} mcg</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="doses-week">Doses per Week</Label>
                    <Input
                      id="doses-week"
                      type="number"
                      placeholder="e.g., 5"
                      value={calculator.dosesPerWeek}
                      onChange={(e) => setCalculator({ ...calculator, dosesPerWeek: e.target.value, result: null })}
                      data-testid="calc-frequency-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    onClick={calculateDosing}
                    className="flex-1 bg-teal-700 hover:bg-teal-800 text-white h-11"
                    data-testid="calc-calculate-btn"
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={resetCalculator}
                    data-testid="calc-reset-btn"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results Card */}
            <Card className={`border-slate-100 ${calculator.result ? 'bg-gradient-to-br from-teal-50/50 to-white' : ''}`}>
              <CardHeader>
                <CardTitle>Your Dosing Protocol</CardTitle>
              </CardHeader>
              <CardContent>
                {calculator.result ? (
                  <div className="space-y-4" data-testid="calc-result">
                    {/* Insulin Units - Main Result */}
                    <div className="p-5 bg-teal-700 rounded-xl text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Syringe className="h-5 w-5" />
                        <span className="text-sm font-medium text-teal-100">Per Dose on 100-Unit Insulin Syringe</span>
                      </div>
                      <p className="text-4xl font-heading font-bold">
                        {calculator.result.insulinUnits} units
                      </p>
                      <p className="text-sm text-teal-200 mt-1">
                        ({calculator.result.volumePerDoseUl} µL = {calculator.result.volumePerDoseMl} mL)
                      </p>
                    </div>

                    {/* Visual Syringe Guide */}
                    <div className="p-4 bg-white rounded-xl border border-slate-200">
                      <p className="text-xs font-medium text-slate-500 mb-3">INSULIN SYRINGE VISUAL (100 units = 1mL)</p>
                      <div className="relative h-8 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="absolute left-0 top-0 bottom-0 bg-teal-500 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(parseFloat(calculator.result.insulinUnits), 100)}%` }}
                        />
                        {/* Tick marks */}
                        {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((tick) => (
                          <div 
                            key={tick}
                            className="absolute top-0 bottom-0 w-px bg-slate-300"
                            style={{ left: `${tick}%` }}
                          />
                        ))}
                        <div 
                          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-teal-700 rounded-full border-2 border-white shadow"
                          style={{ left: `calc(${Math.min(parseFloat(calculator.result.insulinUnits), 100)}% - 6px)` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs text-slate-400">
                        <span>0</span>
                        <span>25</span>
                        <span>50</span>
                        <span>75</span>
                        <span>100</span>
                      </div>
                    </div>

                    {/* Concentration */}
                    <div className="p-4 bg-white rounded-xl border border-teal-100">
                      <div className="flex items-center gap-2 mb-1">
                        <FlaskConical className="h-4 w-4 text-teal-600" />
                        <span className="text-sm font-medium text-slate-600">Solution Concentration</span>
                      </div>
                      <p className="text-xl font-heading font-bold text-teal-700">
                        {calculator.result.concentration} mcg/mL
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {/* Weekly Units */}
                      <div className="p-4 bg-white rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-4 w-4 text-slate-500" />
                          <span className="text-xs font-medium text-slate-500">Weekly Total</span>
                        </div>
                        <p className="text-xl font-heading font-bold text-slate-900">
                          {calculator.result.weeklyUnits} units
                        </p>
                        <p className="text-xs text-slate-400">
                          ({calculator.dosesPerWeek}x per week)
                        </p>
                      </div>

                      {/* Supply Duration */}
                      <div className="p-4 bg-white rounded-xl border border-slate-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Info className="h-4 w-4 text-slate-500" />
                          <span className="text-xs font-medium text-slate-500">Vial Lasts</span>
                        </div>
                        <p className="text-xl font-heading font-bold text-slate-900">
                          {calculator.result.weeksSupply} weeks
                        </p>
                        <p className="text-xs text-slate-400">
                          ({calculator.result.totalDoses} total doses)
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Syringe className="h-16 w-16 mx-auto mb-4 opacity-30" />
                    <p className="font-medium">Select a peptide or enter values</p>
                    <p className="text-sm">Results will show insulin units</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Common Dosing Protocols */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="h-6 w-6 text-teal-700" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Common Weekly Protocols
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                name: "5 Days On / 2 Days Off",
                schedule: ["Mon", "Tue", "Wed", "Thu", "Fri"],
                description: "Most common protocol. Dosing on weekdays with weekends off.",
                color: "teal"
              },
              {
                name: "Every Other Day (EOD)",
                schedule: ["Mon", "Wed", "Fri", "Sun"],
                description: "Alternating days protocol. Good for peptides requiring spacing between doses.",
                color: "blue"
              },
              {
                name: "Daily Protocol",
                schedule: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
                description: "7 days per week dosing. Often used for shorter research cycles.",
                color: "purple"
              }
            ].map((protocol, index) => (
              <Card key={index} className="border-slate-100">
                <CardContent className="p-6">
                  <h3 className="font-heading font-semibold text-slate-900 mb-3">{protocol.name}</h3>
                  <div className="flex gap-1 mb-4">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                      <div 
                        key={day}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-medium ${
                          protocol.schedule.includes(day) 
                            ? 'bg-teal-700 text-white' 
                            : 'bg-slate-100 text-slate-400'
                        }`}
                      >
                        {day.charAt(0)}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600">{protocol.description}</p>
                  <p className="text-xs text-slate-400 mt-2">
                    {protocol.schedule.length} doses per week
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Storage & Safety */}
        <section className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <Thermometer className="h-6 w-6 text-teal-700" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Storage & Safety Guidelines
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Thermometer className="h-5 w-5 text-teal-700" />
                  Storage Temperatures
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 font-bold text-sm">-20°C</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Lyophilized (Powder)</p>
                      <p className="text-sm text-slate-600">Store unreconstituted peptides in freezer. Stable for 24+ months.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-teal-700 font-bold text-sm">2-8°C</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">Reconstituted (Liquid)</p>
                      <p className="text-sm text-slate-600">Refrigerate after mixing. Use within 30 days for best stability.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  Important Precautions
                </h3>
                <ul className="space-y-3">
                  {[
                    "Protect from light—store in original vial or wrap in foil",
                    "Never freeze reconstituted peptides",
                    "Avoid repeated freeze-thaw cycles for powder",
                    "Always use sterile technique when drawing doses",
                    "Discard if solution becomes cloudy or discolored",
                    "Keep away from direct sunlight and heat sources"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="h-4 w-4 text-teal-600 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Quick Reference */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="h-6 w-6 text-teal-700" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">
              Quick Reference
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-slate-100 bg-gradient-to-br from-slate-50 to-white">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Unit Conversions</h3>
                <div className="space-y-2 font-mono text-sm">
                  <p className="text-slate-600">1 mg = <span className="text-teal-700 font-bold">1,000 mcg</span></p>
                  <p className="text-slate-600">1 mL = <span className="text-teal-700 font-bold">1,000 µL</span></p>
                  <p className="text-slate-600">1 mL = <span className="text-teal-700 font-bold">100 units</span> (insulin syringe)</p>
                  <p className="text-slate-600">10 units = <span className="text-teal-700 font-bold">0.1 mL</span> = 100 µL</p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-100 bg-gradient-to-br from-slate-50 to-white">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Common Water Volumes</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">5mg vial →</span>
                    <span className="text-teal-700 font-bold">1-2 mL</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">10mg vial →</span>
                    <span className="text-teal-700 font-bold">2-3 mL</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">15mg vial →</span>
                    <span className="text-teal-700 font-bold">3-5 mL</span>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-3">Use more water for easier measuring of smaller doses</p>
              </CardContent>
            </Card>

            <Card className="border-slate-100 bg-gradient-to-br from-slate-50 to-white">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-slate-900 mb-3">Syringe Guide</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Reconstitution</span>
                    <Badge variant="outline">1-3 mL syringe</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Dosing (larger)</span>
                    <Badge variant="outline">1 mL / 100 unit</Badge>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Dosing (precise)</span>
                    <Badge variant="outline">0.5 mL / 50 unit</Badge>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-3">Insulin syringes recommended for accurate small volumes</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Bottom Disclaimer */}
        <div className="mt-12 p-6 bg-amber-50 border border-amber-200 rounded-xl">
          <div className="flex items-start gap-4">
            <Shield className="h-6 w-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-heading font-semibold text-amber-800 mb-2">Research Use Only</h3>
              <p className="text-sm text-amber-700">
                This guide is provided for educational and laboratory research purposes only. All Genora Peptides products 
                are intended strictly for in-vitro research and are not for human or animal consumption. The information 
                provided does not constitute medical advice. Always follow proper laboratory protocols and safety guidelines.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeptideCalculatorPage;
